/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  CREATE_PENDING_CITIZEN_ACCOUNT,
  EMAIL_PASSWORD_RESET,
  IS_EMAIL_VERIFIED,
  ACTIVATE_EMAIL,
  POST_ACES_ACTIVATION_LINK_EMAIL,
  POST_ACES_ACTIVATE_CITIZEN_ACCOUNT,
  POST_ACES_RESEND_ACTIVATION_LINK_EMAIL,
  POST_ACES_SEND_FORGOT_USERNAME_EMAIL,
  POST_ACES_ACCOUNT_UNLOCK,
  POST_ACES_CHECK_ACCOUNT_ENABLE,
  POST_ACES_SEND_ACCOUNT_UNLOCK_EMAIL,
} from './actionTypes';

import {
  createPendingCitizenAccountUtil,
  emailPasswordResetUtil,
  isEmailVerifiedUtil,
  activateEmailUtil,
  postAcesActivationLinkEmailUtil,
  postAcesActivateCitizenAccountUtil,
  postAcesResendActivationLinkEmailUtil,
  postAcesSendForgotUsernameEmailUtil,
  postAcesAccountUnlockUtil,
  postAcesCheckAccountEnableUtil,
  postAcesSendAccountUnlockEmailUtil,
} from './utils';

/**
 * Contains the action creator functions that update the ACESUserRegistration slice of the store.
 *
 * @namespace ACESUserRegistrationActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_create_pending_citizen_account
   *
   * @param {NewACESUAUserAccount} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static createPendingCitizenAccount = (dispatch, newACESUAUserAccount, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      createPendingCitizenAccountUtil.bind(null, newACESUAUserAccount),
      CREATE_PENDING_CITIZEN_ACCOUNT,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to createPendingCitizenAccount.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetCreatePendingCitizenAccount = (dispatch, data) => {
    const json = {
      type: CREATE_PENDING_CITIZEN_ACCOUNT.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to createPendingCitizenAccount.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetCreatePendingCitizenAccountError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, CREATE_PENDING_CITIZEN_ACCOUNT.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_email_password_reset
   *
   * @param {NewACESResetPasswordDetails} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static emailPasswordReset = (dispatch, newACESResetPasswordDetails, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      emailPasswordResetUtil.bind(null, newACESResetPasswordDetails),
      EMAIL_PASSWORD_RESET,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to emailPasswordReset.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetEmailPasswordReset = (dispatch, data) => {
    const json = {
      type: EMAIL_PASSWORD_RESET.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to emailPasswordReset.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetEmailPasswordResetError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, EMAIL_PASSWORD_RESET.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_is_email_verified
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static isEmailVerified = (dispatch, callback) => {
    ReduxUtils.generateAction(dispatch, isEmailVerifiedUtil, IS_EMAIL_VERIFIED, callback);
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to isEmailVerified.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetIsEmailVerified = (dispatch, data) => {
    const json = {
      type: IS_EMAIL_VERIFIED.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to isEmailVerified.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetIsEmailVerifiedError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, IS_EMAIL_VERIFIED.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_activate_email
   *
   * @param {NewACESHashTokenKey} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static activateEmail = (dispatch, newACESHashTokenKey, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      activateEmailUtil.bind(null, newACESHashTokenKey),
      ACTIVATE_EMAIL,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to activateEmail.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetActivateEmail = (dispatch, data) => {
    const json = {
      type: ACTIVATE_EMAIL.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to activateEmail.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetActivateEmailError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, ACTIVATE_EMAIL.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_activation_link_email
   *
   * @param {NewACESActivationLinkEmailDetails} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static postAcesActivationLinkEmail = (dispatch, newACESActivationLinkEmailDetails, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesActivationLinkEmailUtil.bind(null, newACESActivationLinkEmailDetails),
      POST_ACES_ACTIVATION_LINK_EMAIL,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesActivationLinkEmail.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesActivationLinkEmail = (dispatch, data) => {
    const json = {
      type: POST_ACES_ACTIVATION_LINK_EMAIL.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesActivationLinkEmail.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesActivationLinkEmailError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACES_ACTIVATION_LINK_EMAIL.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_activate_citizen_account
   *
   * @param {NewACESActivateCitizenAccountWithEmailDetails} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static postAcesActivateCitizenAccount = (
    dispatch,
    newACESActivateCitizenAccountWithEmailDetails,
    callback
  ) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesActivateCitizenAccountUtil.bind(null, newACESActivateCitizenAccountWithEmailDetails),
      POST_ACES_ACTIVATE_CITIZEN_ACCOUNT,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesActivateCitizenAccount.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesActivateCitizenAccount = (dispatch, data) => {
    const json = {
      type: POST_ACES_ACTIVATE_CITIZEN_ACCOUNT.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesActivateCitizenAccount.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesActivateCitizenAccountError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACES_ACTIVATE_CITIZEN_ACCOUNT.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_resend_activation_link_email
   *
   * @param {NewACESActivationLinkEmailDetails} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static postAcesResendActivationLinkEmail = (
    dispatch,
    newACESActivationLinkEmailDetails,
    callback
  ) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesResendActivationLinkEmailUtil.bind(null, newACESActivationLinkEmailDetails),
      POST_ACES_RESEND_ACTIVATION_LINK_EMAIL,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesResendActivationLinkEmail.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesResendActivationLinkEmail = (dispatch, data) => {
    const json = {
      type: POST_ACES_RESEND_ACTIVATION_LINK_EMAIL.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesResendActivationLinkEmail.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesResendActivationLinkEmailError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACES_RESEND_ACTIVATION_LINK_EMAIL.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_send_forgot_username_email
   *
   * @param {NewACESForgotUserNameDetails} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static postAcesSendForgotUsernameEmail = (dispatch, newACESForgotUserNameDetails, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesSendForgotUsernameEmailUtil.bind(null, newACESForgotUserNameDetails),
      POST_ACES_SEND_FORGOT_USERNAME_EMAIL,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesSendForgotUsernameEmail.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesSendForgotUsernameEmail = (dispatch, data) => {
    const json = {
      type: POST_ACES_SEND_FORGOT_USERNAME_EMAIL.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesSendForgotUsernameEmail.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesSendForgotUsernameEmailError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACES_SEND_FORGOT_USERNAME_EMAIL.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_account_unlock
   *
   * @param {NewACESAccountUnlockDetails} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static postAcesAccountUnlock = (dispatch, newACESAccountUnlockDetails, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesAccountUnlockUtil.bind(null, newACESAccountUnlockDetails),
      POST_ACES_ACCOUNT_UNLOCK,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesAccountUnlock.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesAccountUnlock = (dispatch, data) => {
    const json = {
      type: POST_ACES_ACCOUNT_UNLOCK.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesAccountUnlock.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesAccountUnlockError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACES_ACCOUNT_UNLOCK.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_check_account_enable
   *
   * @param {NewACESCheckAccountEnabled} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static postAcesCheckAccountEnable = (dispatch, newACESCheckAccountEnabled, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesCheckAccountEnableUtil.bind(null, newACESCheckAccountEnabled),
      POST_ACES_CHECK_ACCOUNT_ENABLE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesCheckAccountEnable.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesCheckAccountEnable = (dispatch, data) => {
    const json = {
      type: POST_ACES_CHECK_ACCOUNT_ENABLE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesCheckAccountEnable.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesCheckAccountEnableError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACES_CHECK_ACCOUNT_ENABLE.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/aces_send_account_unlock_email
   *
   * @param {NewACESAccountUnlockDetails} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static postAcesSendAccountUnlockEmail = (dispatch, newACESAccountUnlockDetails, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesSendAccountUnlockEmailUtil.bind(null, newACESAccountUnlockDetails),
      POST_ACES_SEND_ACCOUNT_UNLOCK_EMAIL,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesSendAccountUnlockEmail.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesSendAccountUnlockEmail = (dispatch, data) => {
    const json = {
      type: POST_ACES_SEND_ACCOUNT_UNLOCK_EMAIL.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesSendAccountUnlockEmail.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESUserRegistrationActions
   */
  static resetPostAcesSendAccountUnlockEmailError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACES_SEND_ACCOUNT_UNLOCK_EMAIL.failureAction);
  };
}
